import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  Lightning,
  ChartLineUp,
  HandCoins,
  Handshake,
  Scales,
  Lock,
  Clock,
  CheckCircle,
  UserPlus,
  FileText,
  Coins,
  WhatsappLogo,
  EnvelopeSimple,
  Phone,
  MapPin,
  List,
  X,
} from "@phosphor-icons/react";

/* ====== Marca Activa Capital Privado ====== */
const BRAND = {
  deep: "#0E4D28", // verde bosque profundo (círculo del logo)
  deep2: "#0A3A1E",
  leaf: "#7FC97A", // verde claro (check del logo)
  leafSoft: "#A7DBA0",
  bg: "#F3F7F3",
  ink: "#0C1F12",
};

const WHATSAPP = "https://wa.me/56951592774";
const EMAIL = "contacto@activacapitalprivado.cl";

const NAV = [
  { href: "#beneficios", label: "Beneficios" },
  { href: "#como-funciona", label: "Cómo funciona" },
  { href: "#contacto", label: "Contacto" },
];

const STATS = [
  { k: "+18 años", v: "de experiencia financiera" },
  { k: "100%", v: "proceso digital y simple" },
  { k: "24/7", v: "acompañamiento con asesor" },
  { k: "UF", v: "créditos a tu medida" },
];

const BENEFITS = [
  {
    icon: Lightning,
    title: "Aprobación ágil",
    desc: "Evaluamos tu solicitud en tiempo récord y sin trámites interminables. Respuesta clara y rápida.",
  },
  {
    icon: Scales,
    title: "Condiciones transparentes",
    desc: "Tasas y cuotas claras desde el primer día. Sin letra chica ni costos ocultos en tu crédito.",
  },
  {
    icon: ChartLineUp,
    title: "Crece tu capital",
    desc: "Asesoría de inversión privada enfocada en hacer rendir tu patrimonio con una estrategia a tu medida.",
  },
  {
    icon: Handshake,
    title: "Asesor dedicado",
    desc: "Una persona real te acompaña en cada paso, por chat, correo o WhatsApp cuando lo necesites.",
  },
  {
    icon: Lock,
    title: "Seguridad primero",
    desc: "Tus datos y operaciones protegidos con los más altos estándares de seguridad y confidencialidad.",
  },
  {
    icon: Clock,
    title: "Disponible siempre",
    desc: "Gestiona tus créditos e inversiones desde cualquier lugar, a cualquier hora, en segundos.",
  },
];

const STEPS = [
  {
    icon: UserPlus,
    title: "Crea tu cuenta",
    desc: "Regístrate en minutos con tu RUT. Sin papeleo y 100% en línea.",
  },
  {
    icon: FileText,
    title: "Solicita o simula",
    desc: "Pide tu crédito o explora opciones de inversión con tu asesor asignado.",
  },
  {
    icon: CheckCircle,
    title: "Recibe tu evaluación",
    desc: "Te entregamos una propuesta clara con tasas, cuotas y plazos a tu medida.",
  },
  {
    icon: Coins,
    title: "Haz crecer tu dinero",
    desc: "Recibe tu crédito o activa tu inversión y sigue todo desde tu panel digital.",
  },
];

function Logo({ className = "" }) {
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <img
        src="/logo-activa.png"
        alt="Activa Capital Privado"
        className="h-11 w-11 object-contain"
      />
      <div className="leading-none">
        <div
          className="text-[1.05rem] font-extrabold tracking-tight"
          style={{ color: BRAND.deep, fontFamily: "Outfit, sans-serif" }}
        >
          Activa
        </div>
        <div
          className="text-[0.6rem] font-semibold tracking-[0.28em] text-slate-500"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          CAPITAL PRIVADO
        </div>
      </div>
    </div>
  );
}

export default function LandingActiva() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div
      className="min-h-screen overflow-x-hidden antialiased"
      style={{ background: BRAND.bg, color: BRAND.ink, fontFamily: "Outfit, sans-serif" }}
    >
      {/* ================= NAVBAR ================= */}
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-white/90 backdrop-blur-md shadow-sm" : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3.5">
          <Logo />

          <nav className="hidden items-center gap-8 md:flex">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                className="text-sm font-medium text-slate-600 transition-colors hover:text-[var(--deep)]"
                style={{ "--deep": BRAND.deep }}
              >
                {n.label}
              </a>
            ))}
          </nav>

          <div className="hidden items-center gap-3 md:flex">
            <Link
              to="/login"
              className="rounded-full px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:text-[var(--deep)]"
              style={{ "--deep": BRAND.deep }}
            >
              Ingresar
            </Link>
            <Link
              to="/register"
              className="group inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-900/20 transition-transform hover:-translate-y-0.5"
              style={{ background: BRAND.deep }}
            >
              Crear cuenta
              <ArrowRight weight="bold" className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>

          <button
            className="rounded-lg p-2 text-slate-700 md:hidden"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Menú"
          >
            {menuOpen ? <X className="h-6 w-6" /> : <List className="h-6 w-6" />}
          </button>
        </div>

        {menuOpen && (
          <div className="border-t border-slate-100 bg-white px-5 py-4 md:hidden">
            <div className="flex flex-col gap-1">
              {NAV.map((n) => (
                <a
                  key={n.href}
                  href={n.href}
                  onClick={() => setMenuOpen(false)}
                  className="rounded-lg px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  {n.label}
                </a>
              ))}
              <div className="mt-2 flex gap-2">
                <Link
                  to="/login"
                  className="flex-1 rounded-full border border-slate-200 px-4 py-2.5 text-center text-sm font-semibold text-slate-700"
                >
                  Ingresar
                </Link>
                <Link
                  to="/register"
                  className="flex-1 rounded-full px-4 py-2.5 text-center text-sm font-semibold text-white"
                  style={{ background: BRAND.deep }}
                >
                  Crear cuenta
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden pt-28 pb-20 md:pt-36 md:pb-28">
        {/* fondo decorativo */}
        <div
          className="pointer-events-none absolute -top-40 right-[-10%] h-[520px] w-[520px] rounded-full blur-3xl opacity-30"
          style={{ background: BRAND.leaf }}
        />
        <div
          className="pointer-events-none absolute -bottom-32 left-[-10%] h-[420px] w-[420px] rounded-full blur-3xl opacity-20"
          style={{ background: BRAND.deep }}
        />

        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 lg:grid-cols-2">
          <div>
            <span
              className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold"
              style={{ background: "#E3F2E1", color: BRAND.deep }}
            >
              <ShieldCheck weight="fill" className="h-4 w-4" />
              Capital privado · Chile
            </span>

            <h1
              className="mt-6 text-4xl font-extrabold leading-[1.05] tracking-tight md:text-6xl"
              style={{ color: BRAND.deep }}
            >
              Impulsa tus{" "}
              <span className="relative whitespace-nowrap">
                <span style={{ color: BRAND.leaf }}>proyectos</span>
              </span>{" "}
              con capital que crece contigo.
            </h1>

            <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-600">
              En <strong style={{ color: BRAND.deep }}>Activa Capital Privado</strong> te acompañamos con
              créditos ágiles e inversión privada inteligente. Todo digital, transparente y con un asesor
              dedicado a tu lado.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/register"
                className="group inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-base font-semibold text-white shadow-xl shadow-emerald-900/20 transition-transform hover:-translate-y-0.5"
                style={{ background: BRAND.deep }}
              >
                Comenzar ahora
                <ArrowRight weight="bold" className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href={WHATSAPP}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full border-2 px-7 py-3.5 text-base font-semibold transition-colors"
                style={{ borderColor: BRAND.deep, color: BRAND.deep }}
              >
                <WhatsappLogo weight="fill" className="h-5 w-5" />
                Hablar con un asesor
              </a>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-slate-500">
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle weight="fill" className="h-4 w-4" style={{ color: BRAND.leaf }} /> Sin papeleo
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle weight="fill" className="h-4 w-4" style={{ color: BRAND.leaf }} /> Respuesta rápida
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle weight="fill" className="h-4 w-4" style={{ color: BRAND.leaf }} /> Asesor dedicado
              </span>
            </div>
          </div>

          {/* imagen hero */}
          <div className="relative">
            <div className="relative overflow-hidden rounded-[2rem] shadow-2xl shadow-emerald-900/20">
              <img
                src="https://images.unsplash.com/photo-1781246435700-afec19012b45?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwzfHxmaW5hbmNpYWwlMjBzdWNjZXNzfGVufDB8fHxncmVlbnwxNzkyMjUwOTgxfDA&ixlib=rb-4.1.0&q=85"
                alt="Crecimiento financiero"
                className="h-[420px] w-full object-cover md:h-[500px]"
              />
              <div
                className="absolute inset-0"
                style={{ background: "linear-gradient(160deg, rgba(14,77,40,0) 40%, rgba(14,77,40,0.35) 100%)" }}
              />
            </div>

            {/* tarjeta flotante */}
            <div className="absolute -bottom-6 -left-4 w-56 rounded-2xl bg-white p-4 shadow-xl md:-left-8">
              <div className="flex items-center gap-3">
                <div
                  className="flex h-10 w-10 items-center justify-center rounded-xl"
                  style={{ background: "#E3F2E1" }}
                >
                  <HandCoins weight="fill" className="h-5 w-5" style={{ color: BRAND.deep }} />
                </div>
                <div>
                  <div className="text-xs text-slate-500">Crédito aprobado</div>
                  <div className="text-lg font-extrabold" style={{ color: BRAND.deep }}>
                    $5.000.000
                  </div>
                </div>
              </div>
            </div>

            <div className="absolute -right-3 top-8 rounded-2xl bg-white px-4 py-3 shadow-xl md:-right-6">
              <div className="flex items-center gap-2">
                <ChartLineUp weight="fill" className="h-5 w-5" style={{ color: BRAND.leaf }} />
                <span className="text-sm font-bold" style={{ color: BRAND.deep }}>
                  +12,4% anual
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* stats */}
        <div className="relative mx-auto mt-20 max-w-6xl px-5">
          <div className="grid grid-cols-2 gap-4 rounded-3xl bg-white p-6 shadow-lg md:grid-cols-4 md:p-8">
            {STATS.map((s) => (
              <div key={s.v} className="text-center">
                <div className="text-2xl font-extrabold md:text-3xl" style={{ color: BRAND.deep }}>
                  {s.k}
                </div>
                <div className="mt-1 text-xs text-slate-500 md:text-sm">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= BENEFICIOS ================= */}
      <section id="beneficios" className="py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5">
          <div className="mx-auto max-w-2xl text-center">
            <span
              className="inline-block rounded-full px-4 py-1.5 text-xs font-semibold"
              style={{ background: "#E3F2E1", color: BRAND.deep }}
            >
              Beneficios
            </span>
            <h2 className="mt-4 text-3xl font-extrabold tracking-tight md:text-4xl" style={{ color: BRAND.deep }}>
              Por qué elegir Activa
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Diseñamos cada producto pensando en tu tranquilidad: rapidez, claridad y cercanía en un solo lugar.
            </p>
          </div>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {BENEFITS.map((b) => (
              <div
                key={b.title}
                className="group rounded-2xl border border-slate-100 bg-white p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                <div
                  className="flex h-13 w-13 items-center justify-center rounded-2xl transition-colors"
                  style={{ background: "#E3F2E1", width: "3.25rem", height: "3.25rem" }}
                >
                  <b.icon weight="fill" className="h-6 w-6" style={{ color: BRAND.deep }} />
                </div>
                <h3 className="mt-5 text-xl font-bold" style={{ color: BRAND.deep }}>
                  {b.title}
                </h3>
                <p className="mt-2 leading-relaxed text-slate-600">{b.desc}</p>
              </div>
            ))}
          </div>

          {/* banda imagen + texto */}
          <div
            className="mt-16 grid items-center gap-8 overflow-hidden rounded-3xl p-2 md:grid-cols-2"
            style={{ background: BRAND.deep }}
          >
            <div className="p-8 md:p-12">
              <h3 className="text-2xl font-extrabold text-white md:text-3xl">
                Tu patrimonio, trabajando para ti.
              </h3>
              <p className="mt-4 text-emerald-50/90">
                Nuestra asesoría de inversión privada busca que tu capital crezca de forma sostenida, con
                estrategias claras y acompañamiento constante. Tú decides, nosotros te guiamos.
              </p>
              <Link
                to="/register"
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold transition-transform hover:-translate-y-0.5"
                style={{ color: BRAND.deep }}
              >
                Quiero empezar
                <ArrowRight weight="bold" className="h-4 w-4" />
              </Link>
            </div>
            <div className="relative h-64 md:h-80">
              <img
                src="https://images.pexels.com/photos/5550910/pexels-photo-5550910.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Crecimiento de capital"
                className="h-full w-full rounded-2xl object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ================= CÓMO FUNCIONA ================= */}
      <section id="como-funciona" className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5">
          <div className="mx-auto max-w-2xl text-center">
            <span
              className="inline-block rounded-full px-4 py-1.5 text-xs font-semibold"
              style={{ background: "#E3F2E1", color: BRAND.deep }}
            >
              Cómo funciona
            </span>
            <h2 className="mt-4 text-3xl font-extrabold tracking-tight md:text-4xl" style={{ color: BRAND.deep }}>
              Tu dinero en marcha en 4 pasos
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Un proceso simple y 100% digital, desde que te registras hasta que tu capital empieza a crecer.
            </p>
          </div>

          <div className="relative mt-16 grid gap-8 md:grid-cols-4">
            {/* línea conectora */}
            <div
              className="absolute left-0 right-0 top-7 hidden h-0.5 md:block"
              style={{ background: "linear-gradient(90deg, #E3F2E1, " + BRAND.leaf + ", #E3F2E1)" }}
            />
            {STEPS.map((s, i) => (
              <div key={s.title} className="relative text-center">
                <div
                  className="relative z-10 mx-auto flex h-14 w-14 items-center justify-center rounded-full text-white shadow-lg"
                  style={{ background: BRAND.deep }}
                >
                  <s.icon weight="fill" className="h-7 w-7" />
                  <span
                    className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold text-white ring-2 ring-white"
                    style={{ background: BRAND.leaf }}
                  >
                    {i + 1}
                  </span>
                </div>
                <h3 className="mt-5 text-lg font-bold" style={{ color: BRAND.deep }}>
                  {s.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-14 text-center">
            <Link
              to="/register"
              className="group inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 text-base font-semibold text-white shadow-xl shadow-emerald-900/20 transition-transform hover:-translate-y-0.5"
              style={{ background: BRAND.deep }}
            >
              Crear mi cuenta gratis
              <ArrowRight weight="bold" className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* ================= CTA + CONTACTO ================= */}
      <section id="contacto" className="py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5">
          <div
            className="relative overflow-hidden rounded-[2.5rem] px-6 py-14 md:px-16 md:py-20"
            style={{ background: "linear-gradient(140deg, " + BRAND.deep + " 0%, " + BRAND.deep2 + " 100%)" }}
          >
            <div
              className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full blur-3xl opacity-20"
              style={{ background: BRAND.leaf }}
            />
            <div className="relative grid gap-10 lg:grid-cols-2">
              <div>
                <h2 className="text-3xl font-extrabold leading-tight text-white md:text-4xl">
                  ¿Listo para dar el siguiente paso?
                </h2>
                <p className="mt-4 max-w-md text-emerald-50/90">
                  Conversa con un asesor de Activa Capital Privado o crea tu cuenta y comienza hoy mismo. Estamos
                  para ayudarte.
                </p>
                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <Link
                    to="/register"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-7 py-3.5 text-base font-semibold transition-transform hover:-translate-y-0.5"
                    style={{ color: BRAND.deep }}
                  >
                    Crear cuenta
                    <ArrowRight weight="bold" className="h-5 w-5" />
                  </Link>
                  <a
                    href={WHATSAPP}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-white/40 px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-white/10"
                  >
                    <WhatsappLogo weight="fill" className="h-5 w-5" />
                    WhatsApp
                  </a>
                </div>
              </div>

              <div className="grid gap-4 self-center">
                <a
                  href={WHATSAPP}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-4 rounded-2xl bg-white/10 p-4 backdrop-blur transition-colors hover:bg-white/15"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/15">
                    <WhatsappLogo weight="fill" className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <div className="text-xs text-emerald-50/70">WhatsApp</div>
                    <div className="font-semibold text-white">+56 9 5159 2774</div>
                  </div>
                </a>
                <a
                  href={`mailto:${EMAIL}`}
                  className="flex items-center gap-4 rounded-2xl bg-white/10 p-4 backdrop-blur transition-colors hover:bg-white/15"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/15">
                    <EnvelopeSimple weight="fill" className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <div className="text-xs text-emerald-50/70">Correo</div>
                    <div className="font-semibold text-white">{EMAIL}</div>
                  </div>
                </a>
                <div className="flex items-center gap-4 rounded-2xl bg-white/10 p-4 backdrop-blur">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/15">
                    <MapPin weight="fill" className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <div className="text-xs text-emerald-50/70">Oficina</div>
                    <div className="font-semibold text-white">
                      Av. El Bosque 0177, Piso 9, Las Condes, Santiago
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= FOOTER ================= */}
      <footer className="border-t border-slate-200 bg-white py-12">
        <div className="mx-auto max-w-7xl px-5">
          <div className="grid gap-8 md:grid-cols-3">
            <div>
              <Logo />
              <p className="mt-4 max-w-xs text-sm leading-relaxed text-slate-500">
                Créditos ágiles e inversión privada inteligente. Capital que crece contigo.
              </p>
            </div>

            <div className="text-sm text-slate-600">
              <h4 className="font-bold" style={{ color: BRAND.deep }}>
                Contacto
              </h4>
              <ul className="mt-4 space-y-2.5">
                <li className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-slate-400" /> +56 9 5159 2774
                </li>
                <li className="flex items-center gap-2">
                  <EnvelopeSimple className="h-4 w-4 text-slate-400" /> {EMAIL}
                </li>
                <li className="flex items-start gap-2">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                  Av. El Bosque 0177, Piso 9, Las Condes, Santiago, RM — Chile
                </li>
              </ul>
            </div>

            <div className="text-sm text-slate-600">
              <h4 className="font-bold" style={{ color: BRAND.deep }}>
                Información legal
              </h4>
              <ul className="mt-4 space-y-2.5">
                <li>
                  <span className="text-slate-400">Razón social</span>
                  <br />
                  ACTIVA S.A.
                </li>
                <li>
                  <span className="text-slate-400">RUT</span>
                  <br />
                  76.724.250-6
                </li>
              </ul>
              <div className="mt-5 flex gap-3">
                <Link
                  to="/login"
                  className="rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-700 hover:border-slate-300"
                >
                  Ingresar
                </Link>
                <Link
                  to="/register"
                  className="rounded-full px-4 py-2 text-xs font-semibold text-white"
                  style={{ background: BRAND.deep }}
                >
                  Crear cuenta
                </Link>
              </div>
            </div>
          </div>

          <div className="mt-10 border-t border-slate-100 pt-6 text-center text-xs text-slate-400">
            © {new Date().getFullYear()} ACTIVA S.A. — RUT 76.724.250-6. Todos los derechos reservados.
          </div>
        </div>
      </footer>
    </div>
  );
}
